/**
 * request-gate: a tiny concurrency cap + in-flight dedupe for async work.
 *
 * Purpose: structural backstop against request storms — a bounded number of
 * `fn()` executions run at once; identical in-flight keys share a single
 * execution. When a slot frees (on resolve OR reject), the next queued task
 * starts.
 *
 * SCOPE: This is a generic primitive. In this app it is wired into `swr.ts` to
 * govern SWR GETs routed through `fetcher`. It does NOT and cannot govern the
 * many native `fetch` mutations elsewhere in the app — a global cap would need
 * a shared `appFetch` migration (separate effort). See `swr.ts`.
 *
 * QUEUE ORDERING: the wait queue is strict FIFO. There is NO interactive-vs-
 * background priority, so on a cold load an interactive/user-initiated GET can
 * queue behind secondary/background GETs that were dispatched first. Adding a
 * priority lane (or tuning `max`) is a follow-up pending live instrumentation.
 *
 * SLOT LEASE (anti-freeze): `fn` here is typically a plain `fetch` with no
 * timeout, and a slot is normally held until `fn` settles. Without a lease, a
 * handful of hung requests would hold every slot indefinitely and freeze the
 * ENTIRE gated read layer (including user-initiated GETs) until the browser's
 * multi-minute network timeout. To prevent that, each running task arms a lease
 * timer: if it hasn't settled within `leaseMs`, the gate FREES ITS SLOT and
 * pumps the next queued task — WITHOUT aborting or rejecting the underlying
 * request, and WITHOUT clearing the inFlight dedupe entry. The request keeps
 * running; its callers still receive the real resolve/reject when it eventually
 * settles. A per-task `slotReleased` flag guarantees the slot is released at
 * most once (either the lease OR the real settle decrements `active`, never
 * both), so `active` cannot go negative and no double-pump occurs.
 */

interface InFlight<T = unknown> {
  promise: Promise<T>;
}

export interface RequestGate {
  /**
   * Run `fn` under the gate, keyed by `key`.
   * - Dedupe: if `key` already has an in-flight execution, returns that same
   *   promise (fn is NOT invoked again, no extra slot consumed).
   * - Cap: at most `max` distinct executions run concurrently; excess queue.
   * The in-flight entry is cleared once it settles (resolve or reject).
   */
  run<T>(key: string, fn: () => Promise<T>): Promise<T>;
}

export function createRequestGate(opts: { max: number; leaseMs?: number }): RequestGate {
  const max = Math.max(1, opts.max);
  // Max time a single task may hold a slot before the slot is leased away to
  // the next queued task (the underlying request keeps running). See header.
  const leaseMs = opts.leaseMs ?? 30_000;

  // Distinct executions currently occupying a slot.
  let active = 0;
  // Keys with an in-flight (running) execution -> the shared promise.
  const inFlight = new Map<string, InFlight>();
  // Tasks waiting for a free slot (distinct keys only).
  const queue: Array<() => void> = [];

  function pump() {
    while (active < max && queue.length > 0) {
      const start = queue.shift()!;
      start();
    }
  }

  function run<T>(key: string, fn: () => Promise<T>): Promise<T> {
    // In-flight dedupe: share the existing execution without a new slot.
    const existing = inFlight.get(key);
    if (existing) {
      return existing.promise as Promise<T>;
    }

    let resolveOuter!: (value: T) => void;
    let rejectOuter!: (reason: unknown) => void;
    const promise = new Promise<T>((resolve, reject) => {
      resolveOuter = resolve;
      rejectOuter = reject;
    });

    // Register the in-flight entry immediately so concurrent callers dedupe
    // even while this task is still queued (not yet occupying a slot).
    inFlight.set(key, { promise });

    const start = () => {
      active += 1;

      // Slot-release guard: the slot is freed at most once, by whichever of
      // the lease timer or the real settle fires first. This prevents `active`
      // from going negative (double-free) and prevents a double `pump()`.
      let slotReleased = false;
      const releaseSlot = () => {
        if (slotReleased) return;
        slotReleased = true;
        active -= 1;
        pump();
      };

      // Anti-freeze lease: if the task hasn't settled within leaseMs, free the
      // slot for the next queued task. The underlying request is NOT aborted or
      // rejected, and the inFlight dedupe entry is left intact — the task still
      // settles for its real callers, it just no longer occupies a slot.
      const leaseTimer = setTimeout(releaseSlot, leaseMs);

      let settled: Promise<T>;
      try {
        settled = fn();
      } catch (err) {
        // Synchronous throw from fn: treat as rejection.
        settled = Promise.reject(err);
      }
      Promise.resolve(settled).then(
        (value) => {
          clearTimeout(leaseTimer);
          inFlight.delete(key);
          resolveOuter(value);
          releaseSlot();
        },
        (reason) => {
          // Free the slot AND clear the key so a retry starts fresh.
          clearTimeout(leaseTimer);
          inFlight.delete(key);
          rejectOuter(reason);
          releaseSlot();
        }
      );
    };

    queue.push(start);
    pump();

    return promise;
  }

  return { run };
}
